// src/pages/PrivacyPolicy.js
import React from 'react';
import './PrivacyPolicy.css';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <div className="privacy-policy-container">
      <h1 className="text-center">Privacy Policy — Graystone Construction &amp; Calimesa Turf Center</h1>
      <p className="text-center"><em>Effective Date: October 23, 2025</em></p>

      <section>
        <h2>1. Who We Are</h2>
        <p>
          Graystone Construction &amp; Calimesa Turf Center (<strong>“Graystone”</strong>, “we”, “us”, or “our”) provides construction and landscape
          services, including artificial turf sales (new and recycled), landscape design and installation, grading, irrigation and drainage work,
          project management, and related consulting. This Privacy Policy explains how we collect, use, disclose, and protect information when you
          visit our websites, use our quote or appointment forms, upload project documentation through our <strong>Project Upload</strong> portal, or
          access any staff, contractor, or customer portals we operate (collectively, the “Portals”).
        </p>
      </section>

      <section>
        <h2>2. Scope</h2>
        <p>
          This Policy applies to: (a) website visitors; (b) homeowners and property owners; (c) licensed contractors and subcontractors; and
          (d) Graystone staff, sales representatives, Home Improvement Salespersons (HIS), and other authorized users who access the Portals.
        </p>
      </section>

      <section>
        <h2>3. Information We Collect</h2>
        <h3>3.1 Information You Provide</h3>
        <ul>
          <li>Identity and contact details (name, phone number, email address, business name, mailing and project addresses).</li>
          <li>
            Project information (property details, photos and videos of existing conditions, measurements, diagrams, plans, preferred materials,
            turf types, budgets, schedules, and other information you choose to share about your project).
          </li>
          <li>
            <strong>Project Uploads:</strong> files you upload (e.g., PDF contracts, change orders, permits, inspection reports, invoices, JPEG/PNG/HEIC
            images, MP4 video, MP3 audio or voice notes), plus file metadata (filename, size, timestamps).
          </li>
          <li>Portal account data (username, hashed password, role, access logs, and internal identifiers used to manage jobs and permissions).</li>
          <li>Communications (SMS/text messages, emails, form submissions, call notes, and, where lawful, recorded calls for training and quality).</li>
        </ul>

        <h3>3.2 Information Collected Automatically</h3>
        <p>
          We automatically collect certain device and usage information when you access our sites or Portals, such as IP address, browser type,
          device identifiers, pages viewed, referring URLs, and timestamps. We use this information for security, troubleshooting, analytics, and
          to maintain audit logs.
        </p>

        <h3>3.3 Information From Third Parties</h3>
        <p>
          Where lawful and appropriate, we may receive information from third parties such as mapping or address-validation services, payment
          processors, financing providers, subcontractors, suppliers, and communication service providers to help us verify information, schedule
          work, process payments, and operate our business.
        </p>
      </section>

      <section>
        <h2>4. How We Use Information</h2>
        <ul>
          <li>Review and respond to quote requests, inquiries, and appointment bookings.</li>
          <li>Schedule virtual and on-site appointments, site visits, and turf pickups.</li>
          <li>Prepare estimates, change orders, contracts, and project documentation.</li>
          <li>Manage and perform construction and landscape projects, including turf installations and related services.</li>
          <li>Operate the Portals, verify identity, manage user roles and permissions, and maintain security and audit logs.</li>
          <li>Process payments, invoicing, financing coordination, and collections where applicable.</li>
          <li>Monitor for fraud and abuse, secure our systems, and respond to incidents.</li>
          <li>Send administrative and service-related messages, and—where permitted—limited marketing communications about our services.</li>
          <li>Comply with legal obligations, record-keeping requirements, and contractual commitments.</li>
        </ul>
      </section>

      <section>
        <h2>5. Legal Basis &amp; Consent</h2>
        <p>
          We process personal data to perform a contract with you (for example, when you request a quote, sign a contract, or schedule a project),
          to take steps at your request prior to entering into a contract, to comply with legal obligations (such as tax, licensing, and record-keeping
          requirements), and for our legitimate interests in operating and improving our services, securing our systems, and communicating with you
          about your projects. Where required by law (for example, for certain marketing SMS or email campaigns), we will obtain your consent, which
          you may withdraw at any time; this will not affect prior processing that was lawful at the time it was performed.
        </p>
      </section>

      <section>
        <h2>6. SMS, Email &amp; Calls</h2>
        <p>
          By providing a phone number or email address, you agree that we may contact you with appointment confirmations, reminders, project updates,
          change order approvals, billing notices, and important service-related information. We may also send limited marketing messages about our
          services where permitted by law. Message frequency may vary; message and data rates may apply. You can opt out of marketing SMS at any time
          by replying STOP or following any unsubscribe instructions provided. Opting out of some communications may affect our ability to provide
          certain services (for example, real-time project updates). We use third-party communication providers to deliver SMS, calls, and email, but
          we do not sell or share your mobile opt-in data or phone numbers to third parties for their own independent marketing purposes.
        </p>
      </section>

      <section>
        <h2>7. Sharing &amp; Disclosures</h2>
        <ul>
          <li>
            <strong>Project delivery:</strong> with employees, HIS representatives, licensed contractors, subcontractors, designers, suppliers, and
            delivery partners as reasonably necessary to plan and complete your project.
          </li>
          <li>
            <strong>Service providers:</strong> with third-party vendors who provide hosting, communications, payment processing, appointment
            scheduling, document storage, analytics, and IT/security services under appropriate confidentiality and data-protection terms.
          </li>
          <li>
            <strong>Legal &amp; compliance:</strong> when required by law, subpoena, court order, regulatory authorities, or to protect the safety,
            rights, or property of our customers, staff, or the public.
          </li>
          <li>
            <strong>Business transfers:</strong> in connection with a merger, acquisition, asset sale, or reorganization, subject to this Policy or
            an equivalent level of protection continuing to apply to your information.
          </li>
        </ul>
      </section>

      <section>
        <h2>8. Role-Based Access &amp; Audit Logs</h2>
        <p>
          Our Portals use <strong>role-based access control (RBAC)</strong>. Users (for example, Admin, Staff, HIS, Designer, Contractor) only have
          access to the information necessary to perform their duties. Access to project records and documents is logged (user identity, time,
          IP/device, and key actions). We review and investigate suspicious activity and may suspend or terminate access where misuse is detected.
        </p>
      </section>

      <section>
        <h2>9. Security</h2>
        <ul>
          <li>Encryption in transit (TLS/HTTPS) for our websites and Portals.</li>
          <li>Encryption at rest for stored project documents and uploads where technically supported.</li>
          <li>Strong password practices and hashing for stored credentials; multi-factor authentication may be used for sensitive roles.</li>
          <li>Role-based and least-privilege access, session timeouts, and automatic logoff on shared or inactive sessions.</li>
          <li>Regular backups, access reviews, and security patching of systems within our control.</li>
        </ul>
        <p>
          <em>No security method is 100% guaranteed.</em> If we discover a security incident that affects your information, we will follow our
          incident-response procedures and notify you where required by applicable law.
        </p>
      </section>

      <section>
        <h2>10. Retention</h2>
        <p>
          We retain project records, communications, and uploads for as long as reasonably necessary to fulfill the purposes described in this Policy,
          to complete projects and warranties, to resolve disputes, to collect fees owed, and to comply with legal, tax, and regulatory requirements.
          When information is no longer needed, we take steps to securely delete, anonymize, or de-identify it in accordance with our data-retention
          practices.
        </p>
      </section>

      <section>
        <h2>11. Cross-Border Transfers</h2>
        <p>
          We primarily operate in the United States, but some service providers that store or process information on our behalf may be located in
          other jurisdictions. Where data is transferred across borders, we use reasonable contractual and technical safeguards appropriate to the
          sensitivity of the information and the legal requirements of the destination.
        </p>
      </section>

      <section>
        <h2>12. Cookies & Analytics</h2>
        <p>
          We use essential cookies for security and functionality, and limited analytics to improve the site. You can control cookies in your
          browser settings. Third‑party tools process data under their own policies.
        </p>
      </section>

      <section>
        <h2>13. Children & Minors</h2>
        <p>
          Our services may involve minors under parental/guardian consent in accordance with applicable law. Accounts for minors are administered by
          authorized guardians and clinical staff.
        </p>
      </section>

      <section>
        <h2>14. Your Rights</h2>
        <p>
          Subject to legal limits, you may request access, correction, update, or deletion of your personal data, and you may object to or restrict
          certain processing. To exercise rights, contact us using the details below. We may require verification of identity and legal authority (for
          example, when requests are made by family/caregivers).
        </p>
      </section>

      <section>
        <h2>15. Third‑Party Links</h2>
        <p>
          Our website may contain links to external sites. We are not responsible for their content or privacy practices. Review their policies
          before providing information.
        </p>
      </section>

      <section>
        <h2>16. Changes to This Policy</h2>
        <p>
          We may update this Policy from time to time. The “Effective Date” above will change when updates are posted. Your continued use of the
          website or Portals after updates constitutes acceptance.
        </p>
      </section>

      <section>
        <h2>17. Contact Us</h2>
        <p>
          Graystone Construction &amp; Calimesa Turf Center<br />
          Corporate Office: 1590 Continental St. #206, San Diego, CA 92154<br />
          Phone: <a href="tel:+16197611113">+1 (619) 761-1113</a><br />
          Email: <a href="mailto:support@graystoneenterprises.com">support@graystoneenterprises.com</a>
        </p>
        <p>
          For related terms governing use of the site and Portals, see our <Link to="/terms">Terms &amp; Conditions</Link>.
        </p>
      </section>
    </div>
  );
}

export default PrivacyPolicy;